export interface PublicationUser {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
}

/** `versionDate` values are Europe/Paris wall clock (`YYYY-MM-DD` or `YYYY-MM-DDTHH:mm`). */
export interface TermsVersion {
  versionDate: string | null;
  inForce: boolean;
  lastPublication: {
    versionDate: string | null;
    /** null when the publisher has since been deleted. */
    publishedBy: PublicationUser | null;
    publishedAt: string;
  } | null;
}

export type TermsVersionStatus =
  | 'scheduled'
  | 'in_force'
  | 'removed'
  | 'replaced'
  | 'deleted';

export interface TermsVersionPublication {
  id: number;
  /** null when this publication removed the date. */
  versionDate: string | null;
  status: TermsVersionStatus;
  publishedBy: PublicationUser | null;
  publishedAt: string;
  deletedBy: PublicationUser | null;
  deletedAt: string | null;
  canDelete: boolean;
  updatedAt: string;
  /** null when nobody edited the text or that user was deleted. */
  updatedBy: PublicationUser | null;
  hasContent: boolean;
  canEdit: boolean;
  /** The version the public terms page shows. */
  displayed: boolean;
}

export const TERMS_LANGUAGES = ['fr', 'en'] as const;
export type TermsLanguage = (typeof TERMS_LANGUAGES)[number];

export interface TermsArticleText {
  title: string;
  /** Markdown. */
  body: string;
}

export interface TermsArticle {
  fr: TermsArticleText;
  en: TermsArticleText;
}

export interface TermsLanguageNotes {
  prevailNote: string;
  footer: string;
}

export interface TermsContent {
  fr: TermsLanguageNotes;
  en: TermsLanguageNotes;
  articles: TermsArticle[];
}

/** Both languages hold the same camelCase keys. */
export type TermsStrings = Record<TermsLanguage, Record<string, string>>;

export interface TermsTemplate {
  html: string;
  strings: TermsStrings;
  updatedAt: string;
  updatedBy: PublicationUser | null;
}

export interface TermsVersionDetail extends TermsVersionPublication {
  content: TermsContent | null;
}

export interface TermsContentEdit {
  id: number;
  previousContent: TermsContent;
  comment: string;
  editedBy: PublicationUser | null;
  editedAt: string;
}

export interface TermsTemplateEdit {
  id: number;
  previousHtml: string;
  previousStrings: TermsStrings;
  comment: string;
  editedBy: PublicationUser | null;
  editedAt: string;
}

export interface TermsPage {
  versionDate: string;
  language: TermsLanguage;
  html: string;
}

export interface TermsPreviewRequest {
  language: TermsLanguage;
  versionDate: string;
  content: TermsContent;
  template?: { html: string; strings: TermsStrings };
}
